/**
 * Public tenant affordability calculator — SEO-optimized, no auth required.
 *
 * Computes:
 *   - Max safe rent (30% of gross income, adjusted for existing debts)
 *   - Stretch rent (35% — for hot markets like Toronto/Vancouver)
 *   - Deposit + first month cash needed
 *   - Runway in months at safe rent based on savings
 *
 * Target keywords: "rent affordability calculator Canada",
 * "how much rent can I afford", "30 percent rule Canadian tenants".
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";
import SoftwareAppSchema from "@/components/seo/SoftwareAppSchema";
import { usePageTitle } from "@/lib/seo";
import { Wallet, Home, DollarSign, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";

const money = (n) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(Math.round(n || 0));

export default function AffordabilityCalculator() {
  usePageTitle("Rent Affordability Calculator — Central Rentals Canada");

  const [inp, setInp] = useState({
    grossMonthly: "",
    otherIncome: "",
    debts: "",
    savings: "",
    creditScore: "",
    depositMultiplier: 1,  // 1 mo Ontario, 0.5 mo BC, 1 mo Alberta
  });

  const set = (k) => (e) => setInp({ ...inp, [k]: e.target.value });

  const reset = () => setInp({
    grossMonthly: "", otherIncome: "", debts: "", savings: "", creditScore: "", depositMultiplier: 1,
  });

  const ready = Number(inp.grossMonthly) > 0;

  const r = useMemo(() => {
    // Clamp inputs to sane domains — same defensive pattern as ROI calc.
    // A tenant typing $500M "gross monthly" or credit score 25000 shouldn't
    // produce nonsense results.
    const clampM = (v) => Math.max(0, Math.min(Number(v) || 0, 1_000_000)); // $1M/mo max
    const clampScore = (v) => {
      const n = Number(v) || 0;
      if (n === 0) return 0;
      return Math.max(300, Math.min(n, 900)); // Canadian credit-score range
    };
    const grossMonthly = clampM(inp.grossMonthly);
    const otherIncome = clampM(inp.otherIncome);
    const debts = clampM(inp.debts);
    const savings = clampM(inp.savings);
    const cs = clampScore(inp.creditScore);

    const totalIncome = grossMonthly + otherIncome;
    const safeRent = totalIncome * 0.30;
    const stretchRent = totalIncome * 0.35;
    const dtiAdjustedSafe = Math.max(0, safeRent - debts * 0.5);
    const dtiPct = totalIncome > 0 ? (debts / totalIncome) * 100 : 0;

    const depositAmount = dtiAdjustedSafe * inp.depositMultiplier;
    const moveInCost = depositAmount + dtiAdjustedSafe;
    // Cap runway display at 999 mo — anything higher is meaningless noise.
    const runwayRaw = dtiAdjustedSafe > 0 ? Math.floor(savings / dtiAdjustedSafe) : 0;
    const savingsRunway = Math.min(runwayRaw, 999);
    const canAffordMoveIn = savings >= moveInCost;

    let tier = "—";
    if (cs >= 800) tier = "Excellent";
    else if (cs >= 720) tier = "Very good";
    else if (cs >= 660) tier = "Good";
    else if (cs >= 600) tier = "Fair";
    else if (cs > 0) tier = "Building";

    // Surface any clamps so the tenant knows we adjusted their input.
    const warnings = [];
    if (Number(inp.grossMonthly) > 1_000_000)
      warnings.push("Gross monthly income capped at $1,000,000");
    if (Number(inp.creditScore) > 900)
      warnings.push("Credit score capped at 900 (Canadian max)");
    if (Number(inp.creditScore) > 0 && Number(inp.creditScore) < 300)
      warnings.push("Credit score floor is 300");

    return {
      totalIncome, safeRent, stretchRent, dtiAdjustedSafe, dtiPct,
      depositAmount, moveInCost, savingsRunway, canAffordMoveIn, tier,
      savings, warnings,
      hasSavings: inp.savings !== "" && Number(inp.savings) > 0,
      hasDebts: inp.debts !== "",
      hasCredit: inp.creditScore !== "" && Number(inp.creditScore) > 0,
    };
  }, [inp]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SoftwareAppSchema />
      <PublicHeader />

      {/* HERO */}
      <section className="relative bg-gradient-to-b from-white to-slate-50 border-b border-slate-100 pt-12 md:pt-20 pb-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-3 py-1">
              <Wallet className="w-3.5 h-3.5"/> Free tenant tool
            </div>
            <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 leading-[1.05]" data-testid="aff-hero-title">
              Rent Affordability Calculator for Canadian tenants
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              See exactly how much rent you can afford in Canada in 2026 — using the 30% rule, adjusted for your debts, and tuned for Ontario, BC, and Alberta deposit rules. Free, no signup, instant results.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#calculator" data-testid="aff-hero-cta" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold">
                <Sparkles className="w-4 h-4"/> Calculate my rent budget
              </a>
              <Link to="/signup?role=tenant" data-testid="aff-hero-demo" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-300 hover:border-slate-400 text-slate-800 text-sm font-bold">
                See it in action <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="py-14">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-8">
          {/* Inputs */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6" data-testid="aff-inputs">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Your finances</h2>
              <button
                type="button"
                onClick={reset}
                data-testid="aff-reset-btn"
                className="text-xs font-semibold text-slate-500 hover:text-slate-800 underline underline-offset-4"
              >
                Clear
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Enter your own numbers. Results update as you type.</p>
            <div className="mt-4 space-y-4">
              <Field label="Gross monthly income (CAD)" value={inp.grossMonthly} onChange={set("grossMonthly")} placeholder="e.g. 5200" testid="input-income"/>
              <Field label="Other monthly income (side jobs, etc.)" value={inp.otherIncome} onChange={set("otherIncome")} placeholder="e.g. 0" testid="input-other"/>
              <Field label="Total monthly debts (credit card, loans, car)" value={inp.debts} onChange={set("debts")} placeholder="e.g. 350" testid="input-debts"/>
              <Field label="Total savings available (CAD)" value={inp.savings} onChange={set("savings")} placeholder="e.g. 8000" testid="input-savings"/>
              <Field label="Credit score (300-900)" value={inp.creditScore} onChange={set("creditScore")} max={900} placeholder="e.g. 720" testid="input-credit"/>
              <div>
                <label className="text-xs font-semibold text-slate-600">Province deposit rule</label>
                <select
                  value={inp.depositMultiplier}
                  onChange={(e) => setInp({ ...inp, depositMultiplier: Number(e.target.value) })}
                  data-testid="input-province"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none bg-white"
                >
                  <option value={1}>Ontario / Alberta / MB (1 month deposit)</option>
                  <option value={0.5}>British Columbia / NS (½ month deposit)</option>
                  <option value={0}>Quebec (no deposit allowed)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3 space-y-4" data-testid="aff-results">
            {!ready ? (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center bg-white" data-testid="aff-empty">
                <Wallet className="w-8 h-8 mx-auto text-slate-300"/>
                <p className="mt-3 font-display text-lg font-bold text-slate-800">Enter your income to see results</p>
                <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">Start with gross monthly income. Everything else refines the estimate.</p>
              </div>
            ) : (
            <>
            <div className="rounded-2xl p-6 bg-gradient-to-br from-sky-500 to-blue-600 text-white">
              <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">Your comfortable rent budget</div>
              <div className="mt-1 font-display text-5xl font-black tracking-tighter tabular-nums" data-testid="result-safe-rent">{money(r.dtiAdjustedSafe)}</div>
              <div className="text-sm opacity-90 mt-1">per month, adjusted for your existing debts</div>
              <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full" style={{ width: `${Math.min(100, (r.dtiAdjustedSafe / r.stretchRent) * 100)}%` }}/>
              </div>
              <div className="mt-2 flex justify-between text-xs opacity-80">
                <span>Safe (30%): {money(r.safeRent)}</span>
                <span>Stretch (35%): {money(r.stretchRent)}</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <MetricCard icon={Home} label="Move-in cost (deposit + 1st mo)" value={money(r.moveInCost)} testid="result-move-in"/>
              <MetricCard icon={ShieldCheck} label="Credit tier" value={r.hasCredit ? r.tier : "—"} testid="result-tier"/>
              <MetricCard icon={DollarSign} label="Debt-to-income ratio" value={r.hasDebts ? `${r.dtiPct.toFixed(1)}%` : "—"} testid="result-dti"/>
              <MetricCard icon={Wallet} label="Savings runway" value={r.hasSavings ? `${r.savingsRunway}${r.savingsRunway === 999 ? "+" : ""} mo` : "—"} testid="result-runway"/>
            </div>

            {r.warnings && r.warnings.length > 0 && (
              <div
                className="rounded-2xl bg-amber-50 border border-amber-300 p-4 text-xs text-amber-900 space-y-1"
                data-testid="aff-validation-warnings"
              >
                <strong>Input adjustments applied:</strong>
                <ul className="list-disc pl-5">
                  {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className={`rounded-2xl p-5 ${r.canAffordMoveIn ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`} data-testid="aff-verdict">
              <div className={`font-display font-bold ${r.canAffordMoveIn ? "text-emerald-800" : "text-amber-800"}`}>
                {r.canAffordMoveIn ? "You’re ready to move in" : `You need ${money(r.moveInCost - r.savings)} more in savings`}
              </div>
              <p className={`mt-1 text-sm ${r.canAffordMoveIn ? "text-emerald-700" : "text-amber-700"}`}>
                {r.canAffordMoveIn
                  ? `Your savings cover deposit + first month at ${money(r.dtiAdjustedSafe)}/mo. Landlords using smart tenant screening like Certn will look at your ${r.tier.toLowerCase()} credit tier and comfortable DTI (${r.dtiPct.toFixed(1)}%) favourably.`
                  : `To move in at ${money(r.dtiAdjustedSafe)}/mo you need ${money(r.moveInCost)} total. Save the difference, then use Central Rentals Canada to build a Rent-on-time Score with every payment.`}
              </p>
            </div>

            <div className="rounded-2xl bg-slate-900 text-white p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-sky-400">Build rental credit</div>
                  <h3 className="mt-1 font-display font-bold text-lg tracking-tight">Every on-time rent payment counts</h3>
                  <p className="mt-1 text-sm text-slate-300">Sign up free to Central Rentals Canada. Track payments, earn badges, and share your Rent-on-time Score when applying for your next place.</p>
                </div>
                <Link to="/signup?role=tenant" data-testid="aff-results-cta" className="flex-none inline-flex items-center gap-1 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-sm font-bold">
                  Sign up free <ArrowRight className="w-4 h-4"/>
                </Link>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </section>

      {/* Direct Answer Blocks */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700">Direct answer</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight">
            How much rent can I afford in Canada in 2026?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            In Canada in 2026, financial advisors recommend spending no more than <strong>30% of gross monthly income</strong> on rent (the &ldquo;30% rule&rdquo;). For a tenant earning 5,200 CAD/month, that&rsquo;s roughly 1,560 CAD/month. In hot markets like Toronto and Vancouver, tenants often stretch to 35% (1,820 CAD in the same example) — but this leaves less margin for savings or debt payments. Central Rentals Canada&rsquo;s free calculator above applies the 30% rule and adjusts for your existing monthly debts.
          </p>

          <div className="mt-8 text-xs uppercase tracking-[0.2em] font-bold text-sky-700">Direct answer</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight">
            How much money do I need to move into a Canadian rental?
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            To move into a Canadian rental in 2026, most tenants need first month&rsquo;s rent plus a security deposit — <strong>2 months of rent total in Ontario and Alberta</strong>, <strong>1.5 months in British Columbia and Nova Scotia</strong>, and just <strong>1 month in Quebec</strong> (no deposit permitted). Additional costs may include tenant insurance (~15-25 CAD/month) and utility hookup fees (50-100 CAD).
          </p>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, max, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      <input
        type="number"
        value={value}
        onChange={onChange}
        placeholder={placeholder || ""}
        min={0}
        max={max}
        data-testid={testid}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm tabular-nums focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none placeholder:text-slate-300"
      />
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="mt-1 text-2xl font-bold tracking-tight text-slate-900 tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}
